'use client';
import React, { useState, useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Sparkles, ArrowRight, Mail, Lock, User, CheckCircle } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useLanguage } from '@/context/LanguageContext';
import { getGuestDream } from '@/components/GuestDreamModal';
import LanguageSelector from '@/components/LanguageSelector';
import { createClient } from '@/lib/supabase/client';
import { getAuthErrorMessage, logUnexpectedAuthError } from '@/lib/authErrorMessage';
import {
  ACCESS_RESTRICTED_MESSAGE,
  ALLOW_PUBLIC_REGISTRATION,
  INVITE_EMAIL_NOT_ALLOWED_CODE,
  INVITE_EMAIL_NOT_ALLOWED_MESSAGE,
  REGISTRATION_DISABLED_MESSAGE,
} from '@/lib/registrationConfig';
import { ACCOUNT_ALREADY_EXISTS_CODE } from '@/contexts/AuthContext';
import { getPasswordResetRedirectUrl } from '@/lib/siteUrl';
import {
  CHECKOUT_PLAN_STORAGE_KEY,
  isPaidPlanId,
  safeInternalPath,
} from '@/lib/safeRedirect';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface SignUpForm {
  fullName: string;
  email: string;
  password: string;
}

type AuthMode = 'login' | 'signup' | 'forgot' | 'invitation';

export default function AuthScreen() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [restoredDream, setRestoredDream] = useState('');
  const [mode, setMode] = useState<AuthMode>('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [accessRestricted, setAccessRestricted] = useState(false);
  const { signIn, signUp } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useLanguage();
  const auth = t?.auth;
  const supabase = createClient();

  // Check for a dream param or session-stored dream
  useEffect(() => {
    const dreamParam = searchParams?.get('dream');
    if (dreamParam) {
      setRestoredDream(decodeURIComponent(dreamParam));
    } else {
      const stored = getGuestDream();
      if (stored) setRestoredDream(stored);
    }
    // Check if redirected from pricing or guest modal to sign up
    const tab = searchParams?.get('tab');
    const modeParam = searchParams?.get('mode');
    const wantsSignup = tab === 'signup' || modeParam === 'signup';

    if (searchParams?.get('access') === 'invitation-only') {
      setAccessRestricted(true);
    }

    if (wantsSignup) {
      // Redirect to signup mode.
      setMode('signup');
    }
  }, [searchParams]);

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const signUpForm = useForm<SignUpForm>({ defaultValues: { fullName: '', email: '', password: '' } });

  const resolvePostAuthPath = (): string => {
    const dreamParam = searchParams?.get('dream');
    if (dreamParam) {
      return `/dream-workspace?dream=${encodeURIComponent(dreamParam)}`;
    }

    const planParam = searchParams?.get('plan');
    if (isPaidPlanId(planParam)) {
      try {
        sessionStorage.setItem(CHECKOUT_PLAN_STORAGE_KEY, planParam);
      } catch {
        /* ignore */
      }
      return `/pricing?plan=${planParam}`;
    }

    try {
      const stored = sessionStorage.getItem(CHECKOUT_PLAN_STORAGE_KEY);
      if (isPaidPlanId(stored)) {
        return `/pricing?plan=${stored}`;
      }
    } catch {
      /* ignore */
    }

    const redirectParam = searchParams?.get('redirectTo') || searchParams?.get('redirect');
    const next = searchParams?.get('next');
    // Public signup: default to Pricing (not workspace). Invited users may still
    // land on /welcome via redirectTo when provided by an allowed deep link.
    const fallback = ALLOW_PUBLIC_REGISTRATION ? '/pricing' : '/welcome';
    return safeInternalPath(redirectParam || next, fallback);
  };

  const handleLoginSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success(t?.success?.updated || 'Welcome back! Redirecting...');
      const target = resolvePostAuthPath();
      setTimeout(() => router.push(target), 500);
    } catch (error: any) {
      const msg = error?.message || '';
      if (msg === ACCESS_RESTRICTED_MESSAGE) {
        setAccessRestricted(true);
      }
      loginForm.setError('email', { message: msg || t?.errors?.generic || 'Invalid credentials. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUpSubmit = async (data: SignUpForm) => {
    if (isLoading) return;
    setIsLoading(true);
    setSignupError('');

    const alreadyExistsMsg =
      auth?.accountAlreadyExists ||
      'An account with this email already exists. Sign in instead.';
    const confirmMsg =
      auth?.emailConfirmationRequired ||
      'Account created! Check your email and click the confirmation link to activate your account, then sign in.';
    const genericFail =
      auth?.signupFailedGeneric || 'Could not create your account. Please try again.';
    const inviteMsg = auth?.inviteEmailNotAllowed || INVITE_EMAIL_NOT_ALLOWED_MESSAGE;

    try {
      const result = await signUp(data.email, data.password, { fullName: data.fullName });

      if (result?.status === 'confirmation_required') {
        setSignupError('');
        toast.success(confirmMsg);
        setMode('login');
        loginForm.setValue('email', data.email);
        return;
      }

      if (result?.status === 'already_exists') {
        signUpForm.setError('email', { message: alreadyExistsMsg });
        setSignupError(alreadyExistsMsg);
        setMode('login');
        loginForm.setValue('email', data.email);
        return;
      }

      toast.success(auth?.signupSuccessSigningIn || 'Account created! Signing you in…');
      try {
        await signIn(data.email, data.password);
        // New public accounts are not sent into the workspace — Pricing/checkout next.
        const target = resolvePostAuthPath();
        setTimeout(() => router.push(target), 500);
      } catch {
        // Account exists; confirmation or session cookie may still be pending
        setMode('login');
        loginForm.setValue('email', data.email);
        toast.success(confirmMsg);
      }
    } catch (error: unknown) {
      const msg = getAuthErrorMessage(error);
      const lower = msg.toLowerCase();

      // Legacy throw shape (if any caller still throws confirmation)
      if (
        (error && typeof error === 'object' && (error as { isEmailConfirmation?: boolean }).isEmailConfirmation) ||
        msg === 'EMAIL_CONFIRMATION_REQUIRED'
      ) {
        setSignupError('');
        toast.success(confirmMsg);
        setMode('login');
        loginForm.setValue('email', data.email);
        return;
      }

      if (
        msg === INVITE_EMAIL_NOT_ALLOWED_CODE ||
        msg === INVITE_EMAIL_NOT_ALLOWED_MESSAGE ||
        msg === REGISTRATION_DISABLED_MESSAGE ||
        lower.includes('invitation list') ||
        lower.includes('invitation only')
      ) {
        signUpForm.setError('email', { message: inviteMsg });
        setSignupError(inviteMsg);
      } else if (
        msg === ACCOUNT_ALREADY_EXISTS_CODE ||
        lower.includes('already registered') ||
        lower.includes('already exists') ||
        lower.includes('email address is already') ||
        lower.includes('already been registered') ||
        lower.includes('user already')
      ) {
        signUpForm.setError('email', { message: alreadyExistsMsg });
        setSignupError(alreadyExistsMsg);
        setMode('login');
        loginForm.setValue('email', data.email);
      } else if (lower.includes('invalid email') || lower.includes('email is invalid') || lower.includes('unable to validate email')) {
        signUpForm.setError('email', { message: 'Please enter a valid email address (e.g. name@gmail.com).' });
      } else if (lower.includes('password') && (lower.includes('short') || lower.includes('characters') || lower.includes('weak'))) {
        signUpForm.setError('password', { message: 'Password must be at least 8 characters long.' });
      } else if (lower.includes('rate limit') || lower.includes('too many')) {
        setSignupError(genericFail);
      } else {
        // Empty AuthRetryableFetchError / unknown objects → never render "{}"
        logUnexpectedAuthError('AuthScreen.signup', error);
        setSignupError(msg || genericFail);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error('Please enter your email address.');
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: getPasswordResetRedirectUrl(),
      });
      if (error) throw error;
      setForgotSent(true);
    } catch (error: any) {
      const message =
        error?.message === 'SITE_URL_NOT_CONFIGURED' ?'Password reset is temporarily unavailable. Please contact support.' : error?.message ||'Failed to send reset email. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const invitationPanel = (
    <div className="flex flex-col items-center gap-5 py-6 text-center">
      <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/25 flex items-center justify-center text-2xl">
        ✉️
      </div>
      <div>
        <h2 className="text-lg font-700 text-foreground mb-2">
          {auth?.invitationOnlyTitle || 'Invitation Only'}
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
          {REGISTRATION_DISABLED_MESSAGE}
        </p>
      </div>
      <div className="flex flex-col w-full gap-3">
        <Link
          href="/private-beta"
          className="btn-primary w-full py-3 rounded-xl text-sm font-600 flex items-center justify-center gap-2"
        >
          {auth?.joinWaitlist || 'Join the waitlist'} <ArrowRight size={15} />
        </Link>
        <Link
          href="/"
          className="w-full py-3 rounded-xl text-sm font-600 border border-border text-foreground hover:bg-muted/20 transition-all flex items-center justify-center"
        >
          {auth?.backToHome || 'Back to Home'}
        </Link>
      </div>
    </div>
  );

  const features = [
    {
      emoji: '🎬',
      label: auth?.featureVideoTitle || 'AI Video Creator',
      desc: auth?.featureVideoDesc || 'Full scene planning',
    },
    {
      emoji: '🎙️',
      label: auth?.featureVoiceTitle || 'Voice Generator',
      desc: auth?.featureVoiceDesc || '40+ voice styles',
    },
    {
      emoji: '📖',
      label: auth?.featureStoryTitle || 'Story Builder',
      desc: auth?.featureStoryDesc || "Children's books & scripts",
    },
    {
      emoji: '📣',
      label: auth?.featureAdTitle || 'Ad Creator',
      desc: auth?.featureAdDesc || 'High-converting copy',
    },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden gradient-hero card-grid-bg flex-col justify-between p-12">
        <div className="absolute inset-0 blob-primary pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-96 h-96 blob-accent pointer-events-none" />

        <div className="relative z-10">
          <div className="flex items-center gap-3.5 mb-10 sm:mb-12 lg:mb-14">
            <AppLogo size={88} className="shrink-0" />
            <span className="font-800 text-2xl sm:text-3xl leading-none text-foreground tracking-tight">
              AIMaker Studio
            </span>
          </div>

          <h2 className="text-hero-md font-800 text-foreground mb-4 max-w-lg">
            {auth?.welcomeTitle || 'Turn Your Ideas Into'}
            <span className="gradient-text"> {auth?.welcomeHighlight || 'Stunning Content'}</span>
          </h2>
          <p className="text-muted-foreground text-base mb-10 max-w-md leading-relaxed">
            {auth?.welcomeDesc || 'Join thousands of creators generating videos, animations, voiceovers, and digital assets with AI — no technical skills needed.'}
          </p>

          {restoredDream && (
            <div className="mb-6 p-4 rounded-xl bg-primary/10 border border-primary/30">
              <p className="text-xs font-600 text-primary mb-1">
                ✨ {auth?.dreamSavedReady || 'Your dream is saved and ready:'}
              </p>
              <p className="text-sm text-foreground italic">"{restoredDream}"</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 max-w-md">
            {features.map((item, i) => (
              <div key={`panel-feat-${i}`} className="glass-card rounded-xl p-4 flex items-start gap-3">
                <span className="text-xl">{item.emoji}</span>
                <div>
                  <p className="text-xs font-600 text-foreground">{item.label}</p>
                  <p className="text-[11px] text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-3 pt-8 border-t border-border/30">
          <div className="flex -space-x-2">
            {['PS', 'MW', 'LO', 'DR', 'KJ'].map((initials, i) => (
              <div
                key={`avatar-${i}`}
                className="w-8 h-8 rounded-full border-2 border-background flex items-center justify-center text-[10px] font-700 text-white"
                style={{ background: ['#7c3aed', '#ea580c', '#059669', '#6366f1', '#db2777'][i] }}
              >
                {initials}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            <span className="text-foreground font-600">{auth?.creatorsCount || '12,400+ creators'}</span> {auth?.creatorsBuilding || 'already building with AI'}
          </p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between gap-3 mb-8">
            <div className="flex items-center gap-3 min-w-0 lg:hidden">
              <AppLogo size={56} className="shrink-0" />
              <span className="font-800 text-xl leading-none text-foreground tracking-tight truncate">
                AIMaker Studio
              </span>
            </div>
            <div className="ml-auto shrink-0">
              <LanguageSelector />
            </div>
          </div>

          {restoredDream && (
            <div className="mb-4 p-3 rounded-xl bg-primary/10 border border-primary/30 lg:hidden">
              <p className="text-xs font-600 text-primary mb-1">
                ✨ {auth?.dreamSaved || 'Your dream is saved:'}
              </p>
              <p className="text-xs text-foreground italic">"{restoredDream}"</p>
            </div>
          )}

          {/* Forgot Password */}
          {mode === 'forgot' && (
            <div className="flex flex-col gap-5">
              <div>
                <h1 className="text-2xl font-700 text-foreground mb-1">Reset Password</h1>
                <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link.</p>
              </div>
              {forgotSent ? (
                <div className="flex flex-col items-center gap-4 py-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-mint/15 flex items-center justify-center">
                    <CheckCircle size={32} className="text-mint" />
                  </div>
                  <div>
                    <p className="font-600 text-foreground mb-1">Check your inbox</p>
                    <p className="text-sm text-muted-foreground">We sent a password reset link to <span className="text-foreground">{forgotEmail}</span></p>
                  </div>
                  <button onClick={() => { setMode('login'); setForgotSent(false); setForgotEmail(''); }} className="text-sm text-primary hover:underline">
                    Back to login
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-500 text-foreground mb-1.5">Email address</label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="input-dark w-full pl-9 pr-4 py-3 rounded-xl text-sm"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleForgotPassword}
                    disabled={isLoading}
                    className="btn-primary w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {isLoading ? (
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : 'Send Reset Link'}
                  </button>
                  <button onClick={() => setMode('login')} className="text-sm text-center text-muted-foreground hover:text-foreground transition-colors">
                    ← Back to login
                  </button>
                </>
              )}
            </div>
          )}

          {/* Login / Sign Up / Invitation */}
          {(mode === 'login' || mode === 'signup' || mode === 'invitation') && (
            <>
              {/* Tab switcher */}
              <div className="flex rounded-xl bg-secondary p-1 mb-6">
                <button
                  onClick={() => setMode('login')}
                  className={`flex-1 py-2 rounded-lg text-sm font-600 transition-all ${mode === 'login' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {auth?.memberLogin || 'Sign In'}
                </button>
                <button
                  type="button"
                  onClick={() => setMode('signup')}
                  className={`flex-1 py-2 rounded-lg text-sm font-600 transition-all ${mode === 'signup' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {auth?.createAccountTab || auth?.signUpBtn || 'Create Account'}
                </button>
              </div>

              {/* Invitation-only notice after blocked login */}
              {mode === 'login' && accessRestricted && (
                <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-4">
                  <p className="text-sm text-amber-200/90 leading-relaxed mb-3">
                    {REGISTRATION_DISABLED_MESSAGE}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Link
                      href="/pricing"
                      className="btn-primary px-4 py-2 rounded-lg text-xs font-600 text-center"
                    >
                      {auth?.viewPlans || 'View Plans'}
                    </Link>
                    <Link
                      href="/"
                      className="px-4 py-2 rounded-lg text-xs font-600 border border-border text-center hover:bg-muted/20 transition-colors"
                    >
                      {auth?.backToHome || 'Back to Home'}
                    </Link>
                  </div>
                </div>
              )}

              {/* Live Platform Banner — login only */}
              {mode === 'login' && (
                <div className="mb-6 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 flex items-start gap-3">
                  <Sparkles size={16} className="text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-600 text-foreground">{auth?.liveTitle || 'AIMaker Studio'}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {auth?.liveDesc || 'Sign in to your account to access your workspace.'}
                    </p>
                  </div>
                </div>
              )}

              {/* LOGIN FORM */}
              {mode === 'login' && (
                <form onSubmit={loginForm.handleSubmit(handleLoginSubmit)} className="flex flex-col gap-5">
                  <div>
                    <h1 className="text-2xl font-700 text-foreground mb-1">{auth?.memberLogin || 'Member Login'}</h1>
                    <p className="text-sm text-muted-foreground">{auth?.signInTitle || 'Sign in to continue building with AIMaker Studio.'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-500 text-foreground mb-1.5">{auth?.emailLabel || 'Email address'}</label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        {...loginForm.register('email', {
                          required: 'Email is required',
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                        })}
                        type="email"
                        placeholder={auth?.emailPlaceholder || 'you@example.com'}
                        autoComplete="email"
                        className="input-dark w-full pl-9 pr-4 py-3 rounded-xl text-sm"
                      />
                    </div>
                    {loginForm.formState.errors.email && (
                      <p className="text-xs text-red-400 mt-1.5">{loginForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-500 text-foreground mb-1.5">{auth?.passwordLabel || 'Password'}</label>
                    <div className="relative">
                      <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        {...loginForm.register('password', { required: 'Password is required' })}
                        type={showPassword ? 'text' : 'password'}
                        placeholder={auth?.password || 'Your password'}
                        autoComplete="current-password"
                        className="input-dark w-full pl-9 pr-10 py-3 rounded-xl text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {loginForm.formState.errors.password && (
                      <p className="text-xs text-red-400 mt-1.5">{loginForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        {...loginForm.register('remember')}
                        type="checkbox"
                        className="w-4 h-4 rounded border-border bg-input accent-primary"
                      />
                      <span className="text-sm text-muted-foreground">{auth?.rememberMe || 'Remember me'}</span>
                    </label>
                    <button type="button" onClick={() => setMode('forgot')} className="text-sm text-primary hover:text-primary/80 font-500 transition-colors">
                      {auth?.forgotPassword || 'Forgot password?'}
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {isLoading ? (
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <>{auth?.signInBtn || 'Sign In'} <ArrowRight size={16} /></>
                    )}
                  </button>

                  <div className="pt-2 text-center">
                    <p className="text-[11px] text-muted-foreground/60">
                      {auth?.poweredBy || 'Powered by DreamDNA™'}
                    </p>
                  </div>
                </form>
              )}

              {/* SIGN UP FORM */}
              {mode === 'signup' && (
                <form onSubmit={signUpForm.handleSubmit(handleSignUpSubmit)} className="flex flex-col gap-5">
                  <div>
                    <h1 className="text-2xl font-700 text-foreground mb-1">
                      {auth?.createAccountTab || 'Create Account'}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      {auth?.signupSubtitle || 'Create your AIMaker Studio account and start building your next dream with DreamDNA™.'}
                    </p>
                  </div>

                  {!ALLOW_PUBLIC_REGISTRATION && (
                  <div className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 flex items-start gap-3">
                    <Sparkles size={16} className="text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-600 text-foreground">
                        {auth?.signupInviteRequiredTitle || 'Invitation required'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {auth?.signupInviteRequiredDesc ||
                          'Only invited emails can register at this time.'}
                      </p>
                    </div>
                  </div>
                  )}
                  {ALLOW_PUBLIC_REGISTRATION && (
                  <div className="rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 flex items-start gap-3">
                    <Sparkles size={16} className="text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-600 text-foreground">
                        Workspace unlocks after payment
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Creating an account does not grant DreamDNA or studio access. Complete Creator or Studio checkout next.
                      </p>
                    </div>
                  </div>
                  )}

                  {/* General signup error banner — never show "{}", blank, or object dumps */}
                  {signupError &&
                    signupError.trim() &&
                    signupError.trim() !== '{}' &&
                    signupError.trim() !== '[object Object]' && (
                    <div className="flex items-start gap-2.5 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3" role="alert">
                      <svg className="w-4 h-4 text-red-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                      <p className="text-sm text-red-300 leading-snug">{signupError}</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-500 text-foreground mb-1.5">{auth?.fullNameLabel || 'Full name'}</label>
                    <div className="relative">
                      <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        {...signUpForm.register('fullName', { required: 'Full name is required' })}
                        type="text"
                        placeholder="Your full name"
                        autoComplete="name"
                        className={`input-dark w-full pl-9 pr-4 py-3 rounded-xl text-sm ${signUpForm.formState.errors.fullName ? 'border-red-500/70' : ''}`}
                      />
                    </div>
                    {signUpForm.formState.errors.fullName && (
                      <p className="flex items-center gap-1.5 text-sm text-red-400 mt-2 font-500">
                        <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        {signUpForm.formState.errors.fullName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-500 text-foreground mb-1.5">Email address</label>
                    <div className="relative">
                      <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        {...signUpForm.register('email', {
                          required: 'Email address is required',
                          pattern: {
                            value: /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/,
                            message: 'Enter a valid email address (e.g. name@gmail.com or name@icloud.com)',
                          },
                        })}
                        type="text"
                        inputMode="email"
                        placeholder="you@example.com"
                        autoComplete="email"
                        className={`input-dark w-full pl-9 pr-4 py-3 rounded-xl text-sm ${signUpForm.formState.errors.email ? 'border-red-500/70' : ''}`}
                      />
                    </div>
                    {signUpForm.formState.errors.email && (
                      <p className="flex items-center gap-1.5 text-sm text-red-400 mt-2 font-500">
                        <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        {signUpForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-500 text-foreground mb-1.5">Password</label>
                    <div className="relative">
                      <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        {...signUpForm.register('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters long' },
                        })}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Min. 8 characters"
                        autoComplete="new-password"
                        className={`input-dark w-full pl-9 pr-10 py-3 rounded-xl text-sm ${signUpForm.formState.errors.password ? 'border-red-500/70' : ''}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {signUpForm.formState.errors.password && (
                      <p className="flex items-center gap-1.5 text-sm text-red-400 mt-2 font-500">
                        <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        {signUpForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {isLoading ? (
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <>Create Account <ArrowRight size={16} /></>
                    )}
                  </button>

                  <p className="text-[11px] text-muted-foreground/60 text-center">
                    By creating an account, you agree to our{' '}
                    <a href="/terms-of-service" className="text-primary hover:underline">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>.
                  </p>
                </form>
              )}

              {/* INVITATION-ONLY (registration disabled) */}
              {mode === 'invitation' && invitationPanel}
            </>
          )}
        </div>
      </div>
    </div>
  );
}